import type { DispatchRecord, PlatformInfo, PostMetrics } from "../api";
import { formatCount } from "../lib/metrics";
import { recentPublished } from "../lib/commandCenter";
import {
  dailySeries,
  platformTotals,
  postBreakdown,
  type PostBreakdown,
} from "../lib/analytics";
import { TrendCard } from "./MiniChart";
import { PlatformIcon } from "./PlatformIcon";

/** Sum one metric field across every platform leg of every record. Absent
 * fields simply don't add — no fabricated zeros. Returns null when nothing
 * anywhere reported that metric, so the tile can show a clean "—". */
function totalFor(history: DispatchRecord[], key: keyof PostMetrics): number | null {
  let sum = 0;
  let seen = false;
  for (const rec of history) {
    if (!rec.metrics) continue;
    for (const leg of Object.values(rec.metrics)) {
      const v = leg?.[key];
      if (typeof v === "number") {
        sum += v;
        seen = true;
      }
    }
  }
  return seen ? sum : null;
}

function fmt(n: number | null): string {
  return n === null ? "—" : formatCount(n);
}

function whenLabel(at: number): string {
  return new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The one-line takeaway: which platform carried this post's engagement. */
function insight(b: PostBreakdown): string {
  if (!b.top || b.totalEngagement === 0) return "No engagement reported yet.";
  if (b.shares.length === 1) return `All engagement came from ${b.top.name}.`;
  return `${b.top.name} drove ${Math.round(b.top.pct)}% of engagement.`;
}

/** One post, told as a cross-platform campaign rather than five separate rows. */
function CrossPlatformCard({ b }: { b: PostBreakdown }) {
  return (
    <li className="xp-card">
      <div className="xp-card-head">
        <div className="xp-headings">
          <div className="xp-title">{b.title || "Untitled post"}</div>
          <div className="xp-date">{whenLabel(b.at)}</div>
        </div>
        <div className="xp-totals">
          {b.totalViews !== null && (
            <span className="xp-total">
              <b>{formatCount(b.totalViews)}</b> reach
            </span>
          )}
          <span className="xp-total">
            <b>{formatCount(b.totalEngagement)}</b> engagement
          </span>
        </div>
      </div>

      {b.shares.length > 0 && (
        <>
          <div
            className="xp-bar"
            role="img"
            aria-label={b.shares.map((s) => `${s.name} ${Math.round(s.pct)} percent`).join(", ")}
          >
            {b.shares.map((s) => (
              <span
                key={s.id}
                className="xp-bar-seg"
                style={{ width: `${s.pct}%`, background: s.accent }}
                title={`${s.name} · ${Math.round(s.pct)}%`}
              />
            ))}
          </div>
          <div className="xp-legend">
            {b.shares.map((s) => (
              <span key={s.id} className="xp-legend-item">
                <span className="xp-dot" style={{ background: s.accent }} aria-hidden="true" />
                {s.name} {Math.round(s.pct)}%
              </span>
            ))}
          </div>
        </>
      )}
      <div className="xp-insight">{insight(b)}</div>
    </li>
  );
}

/**
 * Analytics as cross-platform intelligence: aggregate KPI tiles across all
 * posts, then — the point of Broadside — each post told as one campaign that
 * ran on several platforms, so you can see which adaptation carried it. Every
 * number is real; a post with no reported engagement says so plainly.
 */
export function Analytics({
  history,
  platforms,
}: {
  history: DispatchRecord[];
  platforms: PlatformInfo[];
}) {
  const publishedCount = history.filter((r) => r.results.some((x) => x.ok)).length;
  const tiles: { label: string; value: string }[] = [
    { label: "Posts published", value: String(publishedCount) },
    { label: "Total views", value: fmt(totalFor(history, "views")) },
    { label: "Total likes", value: fmt(totalFor(history, "likes")) },
    { label: "Total comments", value: fmt(totalFor(history, "comments")) },
    { label: "Total shares", value: fmt(totalFor(history, "shares")) },
  ];

  const breakdowns = recentPublished(history, 12).map((rec) => postBreakdown(rec, platforms));

  // 30-day trend sparklines + a which-platform-performs bar chart.
  const series = dailySeries(history, 30);
  const viewsTotal = series.views.reduce((a, b) => a + b, 0);
  const engagementTotal = series.engagement.reduce((a, b) => a + b, 0);
  const ptotals = platformTotals(history, platforms);
  const maxEngagement = Math.max(1, ...ptotals.map((p) => p.engagement));

  return (
    <>
      <div className="cc-analytics-grid">
        {tiles.map((t) => (
          <div key={t.label} className="cc-metric-tile">
            <div className="cc-metric-tile-label">{t.label}</div>
            <div className="cc-metric-tile-value">{t.value}</div>
          </div>
        ))}
      </div>

      <section className="cc-perf">
        <div className="cc-perf-head">
          <h2>Trends</h2>
          <span className="muted small">Last 30 days</span>
        </div>
        <div className="trend-grid">
          <TrendCard label="Posts published" points={series.posts} />
          {viewsTotal > 0 && <TrendCard label="Views" points={series.views} />}
          {engagementTotal > 0 && <TrendCard label="Engagement" points={series.engagement} />}
        </div>
      </section>

      {ptotals.length > 0 && (
        <section className="cc-perf">
          <div className="cc-perf-head">
            <h2>By platform</h2>
            <span className="muted small">Engagement across all your posts</span>
          </div>
          <ul className="plat-bars">
            {ptotals.map((p) => (
              <li key={p.id} className="plat-bar-row">
                <span className="plat-bar-name">
                  {(() => {
                    const info = platforms.find((pf) => pf.id === p.id);
                    return info ? <PlatformIcon platform={info} size={16} /> : null;
                  })()}
                  {p.name}
                </span>
                <div
                  className="plat-bar-track"
                  role="img"
                  aria-label={`${p.name}: ${formatCount(p.engagement)} engagement`}
                >
                  <span
                    className="plat-bar-fill"
                    style={{ width: `${(p.engagement / maxEngagement) * 100}%`, background: p.accent }}
                  />
                </div>
                <span className="plat-bar-val">{formatCount(p.engagement)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="cc-perf xp-section">
        <div className="cc-perf-head">
          <h2>By post</h2>
          <span className="muted small">Which platform carried each one</span>
        </div>
        {breakdowns.length === 0 ? (
          <p className="cc-perf-empty">
            Publish your first post and its cross-platform breakdown shows up here.
          </p>
        ) : (
          <ul className="xp-list">
            {breakdowns.map((b) => (
              <CrossPlatformCard key={b.id} b={b} />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
